import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import * as types from '../../../../../constants/actionTypes';
import Drawer from '../../../../Common/Drawer/Drawer';
import Notification from '../../../../Common/Notification';
import DatePicker from '../../../../Common/DatePicker/DatePicker';
import LoadingIndicator from '../../../../Common/LoadingIndicator/LoadingIndicator';
import Moment from 'moment';
import { actions as projectActions } from '../../../../../actions/projectActions';
import { actions as projectLocationActions } from '../../../../../actions/projectLocationActions';
import { ProjectLocation } from '../../../../../types/ProjectLocation';
import { DATE_FORMAT } from '@Constants/constants';

interface Props {
	projectLocationActions: typeof projectLocationActions;
	projectActions: typeof projectActions;
	selectedProjectLocations: ProjectLocation[];
	projectKey: string;
	onComplete: (skippedSomeLocations: boolean) => void;
	onCancel: () => void;
}

const ChangeZeroDateDrawer: React.FC<Props> = ({
	projectLocationActions,
	projectActions,
	selectedProjectLocations,
	projectKey,
	onComplete,
	onCancel,
}) => {
	const [selectedZeroDate, setSelectedZeroDate] = useState<string>('');
	const [isFormValid, setIsFormValid] = useState<boolean>(true);
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [projectEpicLabels, setProjectEpicLabels] = useState<{ id: string; name: string; value?: string }[]>([]);
	const [epicDates, setEpicDates] = useState<{ projectEpicLabelId: string; value: Moment.Moment }[]>([]);

	useEffect(() => {
		const fetchProject = async () => {
			try {
				const result: any = await projectActions.getProject(projectKey);
				if (result.type === types.GET_PROJECT_SUCCESS) {
					setProjectEpicLabels(result.payload.projectEpicLabels);
				} else {
					Notification('error', 'An Error Occurred', result.payload.message);
				}
			} catch (error) {
				Notification('error', 'An Error Occurred', String(error));
			}
		};
		fetchProject();
	}, [projectActions, projectKey]);

	const onSave = async () => {
		if (selectedZeroDate) {
			try {
				const response: any = await projectLocationActions.updateZeroDate(
					selectedProjectLocations.map((m) => m.id),
					projectKey,
					selectedZeroDate,
					epicDates
				);
				if (response.type === types.UPDATE_PROJECTLOCATION_ZERODATE_SUCCESS) {
					Notification('success', 'Success', 'Zero Date updated successfully');
					onComplete(response.payload.skippedSomeLocations);
				} else {
					Notification('error', 'An Error Occurred', response.payload.message);
					setIsSaving(false);
				}
			} catch (error) {
				Notification('error', 'Error', String(error));
			}
		} else {
			setIsFormValid(false);
		}
	};

	const onChangeZeroDate = (date: string) => {
		setSelectedZeroDate(date);
	};

	const onChangeEpicDate = (calendarDate: string, epicLabel: string) => {
		const date = Moment.utc(calendarDate);
		setEpicDates((prevEpicDates) => {
			const labelIndex = prevEpicDates.findIndex((label) => label.projectEpicLabelId === epicLabel);
			if (labelIndex !== -1) {
				return prevEpicDates.map((label, index) => (index === labelIndex ? { ...label, value: date } : label));
			} else {
				return [...prevEpicDates, { projectEpicLabelId: epicLabel, value: date }];
			}
		});
	};

	return (
		<div>
			<LoadingIndicator isLoading={isSaving}>
				<Drawer isWide={false} title='Change Dates' onSaveClick={onSave} saveText='Save' onCancelClick={onCancel}>
					<form noValidate className={isFormValid ? '' : 'was-validated'}>
						<DatePicker
							value={selectedZeroDate ?? ''}
							name='zeroDate'
							label='Zero Date'
							onChange={(date) => onChangeZeroDate(date)}
							isRequired={true}
							errorMessage='Zero Date Required'
						/>
						{
							projectEpicLabels.map((epicLabel, index) => {
														const epicDateObj = epicDates.find(
															d => d.projectEpicLabelId === epicLabel.id
														);

														const value = epicDateObj?.value ? epicDateObj.value.format(DATE_FORMAT) : '';
					
														return (
															<DatePicker
																key={index}
																value={value}
																name={`${epicLabel.id}`}
																label={epicLabel.name}
																onChange={(date) => onChangeEpicDate(date, epicLabel.id)}
															/>
														);
													})
						}

					</form>
				</Drawer>
			</LoadingIndicator>
		</div>
	);
};

const mapDispatchToProps = (dispatch: Dispatch) => {
	return {
		projectActions: bindActionCreators(projectActions, dispatch),
		projectLocationActions: bindActionCreators(projectLocationActions, dispatch),
	};
};

export default connect(null, mapDispatchToProps)(ChangeZeroDateDrawer);
